import { useEffect } from 'react';
import {
  ToastContainer,
  toast as toastify,
  ToastPosition,
} from 'react-toastify';
import { Subject } from 'rxjs';

import 'react-toastify/dist/ReactToastify.css';
import * as styles from './styles.module.scss';
import { Error, Info, Success, Warning } from './icons';

export type ToastData = { msg?: string };
export type ToastMethodFunc = 'success' | 'error' | 'warning' | 'info';

const toastObserver = new Subject<ToastData & { func: ToastMethodFunc }>();

const listenToast = () => {
  const mapFuncToHandled: Record<ToastMethodFunc, Function> = {
    success: toastify.success,
    error: toastify.error,
    info: toastify.info,
    warning: toastify.warning,
  };

  toastObserver.subscribe({
    next: (v) => {
      try {
        mapFuncToHandled[v.func](v.msg);
      } catch (error) {
        console.error('Cannot find toast function', error);
      }
    },
  });
};

type ToastType = 'NORMAL' | 'TYPE_1';

const styleByType: Record<ToastType, string> = {
  NORMAL: '',
  TYPE_1: styles.toastType1,
};

type ToastConfig = {
  type: ToastType;
  position: ToastPosition;
};

export default function Root(props?: Partial<ToastConfig>) {
  useEffect(() => {
    listenToast();
  }, []);

  return (
    <ToastContainer
      position={props?.position || 'bottom-right'}
      autoClose={5000}
      hideProgressBar
      newestOnTop={false}
      closeOnClick
      rtl={false}
      pauseOnFocusLoss
      draggable
      pauseOnHover
      theme="colored"
      className={styleByType[props?.type || 'NORMAL']}
      icon={({ type }) => {
        switch (type) {
          case 'info':
            return <Info />;
          case 'error':
            return <Error />;
          case 'success':
            return <Success />;
          case 'warning':
            return <Warning />;
          default:
            return null;
        }
      }}
    />
  );
}

export const alert: Record<ToastMethodFunc, (d: ToastData) => void> = {
  success: (data: ToastData) => {
    toastObserver.next({ ...data, func: 'success' });
  },
  error: (data: ToastData) => {
    toastObserver.next({ ...data, func: 'error' });
  },
  info: (data: ToastData) => {
    toastObserver.next({ ...data, func: 'info' });
  },
  warning: (data: ToastData) => {
    toastObserver.next({ ...data, func: 'warning' });
  },
};
